import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird';
import type { ReleaseSetup, ReleaseSongSection } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { Panel, PanelHead } from '../../../components/primitives/Panel';
import { useOpenSongPage } from '../../../../smartLinks/catalog/song/page/useOpenSongPage';
import { useOpenPresaveWizardForSong } from '../../../../smartLinks/wizards/presaveLink/useOpenPresaveWizardForSong';
import { useOpenSmartLinkSheetForSong } from '../../../../smartLinks/creationSheet/useOpenSmartLinkSheetForSong';
import { releaseSetupItemNames, releaseSetupMessages } from '../setup/releaseSetupMessages';
import { makeActionOf, SetupRow } from './SetupRow';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SetupPanelProps {
  catalogItemUuid: string;
  setup: ReleaseSetup;
}

/**
 * The setup check of the release (design b1, b3): the master, the cover and the date, the lyrics with
 * their timing, the SmartLink and the pre-save. Complete: one line of ticks. Missing: one row per item
 * with its reason and "Fix in Catalog", which opens the Catalog song page on its section. No setup
 * form shows here (owner rule 2026-10-06: only the song page changes the song). A missing pre-save
 * or SmartLink gets one click that makes it (QA finding F03, decision D10): "Make the pre-save
 * page" opens the pre-save screen with this song picked; for a released song "Make the SmartLink"
 * opens the SmartLink creation sheet with this song picked.
 */
const SetupPanelComponent: React.FC<SetupPanelProps> = ({ catalogItemUuid, setup }) => {
  const intl = useIntl();
  const openSongPage = useOpenSongPage();
  const open = useCallback(
    (section?: ReleaseSongSection) => openSongPage(catalogItemUuid, section),
    [catalogItemUuid, openSongPage]
  );
  const openCatalog = useCallback(() => open(), [open]);
  const openPresave = useOpenPresaveWizardForSong();
  const openSmartLinkSheet = useOpenSmartLinkSheetForSong();
  const makePresave = useCallback(() => openPresave(catalogItemUuid), [catalogItemUuid, openPresave]);
  const makeSmartLink = useCallback(() => openSmartLinkSheet(catalogItemUuid), [catalogItemUuid, openSmartLinkSheet]);
  // The pre-save item shows only while the song is upcoming (fym-shared `releaseSetupCheck`).
  const isUpcoming = setup.items.some((item) => item.key === 'presave');

  if (setup.complete) {
    return (
      <Panel as="section" data-testid="release-setup">
        <PanelHead
          title={intl.formatMessage(releaseSetupMessages.title)}
          subtitle={intl.formatMessage(releaseSetupMessages.subtitle)}
          right={
            <span className={styles.meta}>
              {intl.formatMessage(releaseSetupMessages.complete)}
              <button type="button" className={styles.textLink} onClick={openCatalog}>
                {intl.formatMessage(releaseSetupMessages.openInCatalog)}
              </button>
            </span>
          }
        />
        <div className={styles.missItem}>
          <div className={styles.ticks}>
            {setup.items.map((item) => (
              <span key={item.key} className={styles.tick}>
                <FontAwesomeIcon
                  icon={item.state === 'working' ? faSpinnerThird : faCheck}
                  className={styles.tickMark}
                />
                {intl.formatMessage(releaseSetupItemNames[item.key])}
              </span>
            ))}
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel as="section" data-testid="release-setup">
      <PanelHead
        title={intl.formatMessage(releaseSetupMessages.title)}
        subtitle={intl.formatMessage(releaseSetupMessages.missingSubtitle, { count: setup.missing.length })}
        right={<span className={styles.meta}>{intl.formatMessage(releaseSetupMessages.incomplete)}</span>}
      />
      <div className={styles.missList}>
        {setup.items.map((item) => (
          <SetupRow
            key={item.key}
            item={item}
            onFix={open}
            make={makeActionOf(item, isUpcoming)}
            onMakePresave={makePresave}
            onMakeSmartLink={makeSmartLink}
          />
        ))}
      </div>
    </Panel>
  );
};

export const SetupPanel = TypedMemo(SetupPanelComponent);
